import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { createSelector } from "reselect";

import ActionButton from "components/Buttons/ActionButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { toast } from "react-toastify";
import {
  exportItemCSV_v2,
  fetchItems as OnfetchItems,
  updateStatusInItem as onUpdateStatusInItem,
} from "slices/item/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const Item = () => {
  document.title = "Item List";
  const dispatch = useDispatch<any>();
  const location = useLocation();

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: "item",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  const selectItem = createSelector(
    (state: any) => state.ItemReducer || [],
    (item) => ({
      items: item.itemList,
      rowCount: item.rowCount,
      loading: item.loading,
      error: item.error,
    })
  );
  const { items, rowCount, loading, error } = useSelector(selectItem);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.item
  );

  const [data, setData] = useState([]);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const isFirstRender = useRef(true);

  useEffect(() => {
    dispatch(
      OnfetchItems({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    colValue,
  ]);

  useEffect(() => {
    setData(items);
  }, [items]);

  // Mark first render as complete
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, []);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // Debounce search input to actual search
  // Only update search if searchInput actually changed (not on initial restore)
  useEffect(() => {
    // Skip if searchInput matches search (already in sync, likely from restore)
    if (searchInput === search) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch, search]);

  const handleStatusToggle = useCallback(
    async (item: any) => {
      try {
        const updatedStatus = !item.is_active;
        const updatedDist = { id: item.item_id, is_active: updatedStatus };

        await dispatch(onUpdateStatusInItem(updatedDist)).unwrap();

        toast.success(
          `Item ${updatedStatus ? "activated" : "deactivated"} successfully`
        );

        dispatch(
          OnfetchItems({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: colValue,
          })
        );
      } catch (err: any) {
        toast.error("Failed to update status");
        console.error(err);
      }
    },
    [dispatch, pageIndex, pageSize, sortField, sortDirection, search, colValue]
  );

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await exportItemCSV_v2({
        search,
        filters: colValue,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: EditIcon,
        label: "Edit",
        permission: canUpdate,
        link: (item: any) => `/edit-item/${item.item_id}`,
        color: "primary",
      },
      {
        icon: VisibilityIcon,
        label: "Details",
        permission: canView,
        link: (item: any) => `/item-details/${item.item_id}`,
        color: "info",
      },
    ];
    return items;
  }, [canUpdate, canView]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canUpdate || canView;

    if (!hasPermission) return [];

    return [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [canUpdate, canView, menuItems]);

  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        id: "sr_no", // important: since there's no accessorKey
        accessorKey: "sr_no",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => {
          return row.original.sr_no || row.index + 1 + pageIndex * pageSize;
        },
      },
      {
        header: "Item Name",
        accessorKey: "item_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/item/dropdown/item_name",
        // filterType: "dropdown",
        // filterOptions: ["Active", "Inactive"],
        // filterOptions: [...new Set(data.map((pkg) => pkg.state_name))],
      },
      {
        header: "Alternate Name",
        accessorKey: "alternate_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/item/dropdown/alternate_name",
        cell: ({ row }: { row: any }) => row.original.alternate_name || "-",
      },
      {
        header: "Item Code",
        accessorKey: "item_code",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/item/dropdown/item_code",
        cell: ({ row }: { row: any }) => row.original.item_code || "-",
      },
      {
        header: "Category",
        accessorKey: "category_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/item/dropdown/category_name",
        cell: ({ row }: { row: any }) =>
          row.original.category?.category_name || "-",
      },
      {
        header: "Sub Category",
        accessorKey: "sub_category_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/item/dropdown/sub_category_name",
        cell: ({ row }: { row: any }) =>
          row.original.sub_category?.sub_category_name || "-",
      },
      {
        header: "Color",
        accessorKey: "color",
        enableColumnFilter: true,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterOptions: ["Black", "White"],
        filterFetchUrl: "/master/item/dropdown/color",
        cell: ({ row }: { row: any }) => row.original.color || "-",
      },
      {
        header: "Unit",
        accessorKey: "unit_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/item/dropdown/unit_name",
        cell: ({ row }: { row: any }) => row.original.unit?.unit_name || "-",
      },
      {
        header: "HSN Code",
        accessorKey: "hsn_code",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/item/dropdown/hsn_code",
        cell: ({ row }: { row: any }) => row.original.hsn_code?.hsn_code || "-",
      },
      {
        header: "GST%",
        accessorKey: "gst_percentage",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/item/dropdown/gst_percentage",
        cell: ({ row }: { row: any }) =>
          row.original.hsn_code?.gst_percent?.gst_percent || "-",
      },
      {
        header: "Selling Price",
        accessorKey: "selling_price",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/item/dropdown/selling_price",
        cell: ({ row }: { row: any }) => row.original.selling_price || "-",
      },
      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const item = row.original;
          return (
            <StatusToggle
              isActive={item.is_active}
              onChange={() => {
                handleStatusToggle(item);
              }}
              disabled={!canUpdate}
            />
          );
        },
      },
      {
        header: "Remarks",
        accessorKey: "remark",
        enableColumnFilter: false,
        enableSorting: false,
        maxSize: 250,
        cell: ({ row }: { row: any }) => (
          <TruncatedTextCell
            text={row?.original?.remark}
            maxWidth="300px"
            minWidth="200px"
            maxLines={4}
            rowIndex={row.index}
            tooltipId={`remarks-tooltip-${row.index}`}
          />
        ),
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },

      ...DynamicAction,
    ],
    [DynamicAction, handleStatusToggle, pageIndex, pageSize]
  );

  return (
    <ListingPageContainer>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Item</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}
                    {canCreate && (
                      <ActionButton
                        label="Add Item"
                        icon="bi bi-plus-circle"
                        navigateLink="/add-item"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <TableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setColValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  manualSorting={true}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  //filterOptions={filterOptions}
                  SearchPlaceholder="Search Items..."
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default Item;
